import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Plus, Pencil, Trash2, Merge, Save, X, AlertTriangle, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { format } from "date-fns";

interface Tag {
  id: string;
  name: string;
  group: string;
  created_at: string;
  resourceCount?: number;
}

export default function Tags({ embedded = false }: { embedded?: boolean }) {
  const { organization, isLoading: orgLoading } = useOrganization();
  const [tags, setTags] = useState<Tag[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [editName, setEditName] = useState("");
  const [editGroup, setEditGroup] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [showNewGroupInput, setShowNewGroupInput] = useState(false);
  
  // Create tag dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagGroup, setNewTagGroup] = useState("");
  
  // Merge dialog
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [mergeSourceTag, setMergeSourceTag] = useState<Tag | null>(null);
  const [mergeTargetId, setMergeTargetId] = useState("");
  
  // Delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTag, setDeleteTag] = useState<Tag | null>(null);

  useEffect(() => {
    if (!orgLoading && organization?.id) {
      loadTags();
    }
  }, [orgLoading, organization?.id]);

  const loadTags = async () => {
    if (!organization?.id) return;
    
    setLoading(true);
    
    // Fetch all tags for this organization
    const { data: tagsData, error: tagsError } = await supabase
      .from("tags")
      .select("*")
      .eq("organization_id", organization.id)
      .order("group")
      .order("name");

    if (tagsError) {
      toast.error("Failed to load tags");
      setLoading(false);
      return;
    }

    const tagIds = tagsData?.map(t => t.id) || [];

    // Fetch resource counts for these tags
    const { data: resourceCounts } = tagIds.length > 0
      ? await supabase
          .from("resource_tags")
          .select("tag_id")
          .in("tag_id", tagIds)
      : { data: [] };

    const countMap = new Map<string, number>();
    resourceCounts?.forEach(rt => {
      countMap.set(rt.tag_id, (countMap.get(rt.tag_id) || 0) + 1);
    });

    const tagsWithCounts = (tagsData || []).map(tag => ({
      ...tag,
      resourceCount: countMap.get(tag.id) || 0
    }));

    setTags(tagsWithCounts);

    // Extract unique groups
    const uniqueGroups = [...new Set(tagsData?.map(t => t.group) || [])].sort();
    setGroups(uniqueGroups);
    
    setLoading(false);
  };

  const handleStartEdit = (tag: Tag) => {
    setEditingTag(tag);
    setEditName(tag.name);
    setEditGroup(tag.group);
    setShowNewGroupInput(false);
    setNewGroupName("");
  };

  const handleCancelEdit = () => {
    setEditingTag(null);
    setEditName("");
    setEditGroup("");
    setShowNewGroupInput(false);
    setNewGroupName("");
  };

  const handleSaveEdit = async () => {
    if (!editingTag) return;
    
    const finalGroup = showNewGroupInput && newGroupName.trim() ? newGroupName.trim() : editGroup;
    
    if (!editName.trim() || !finalGroup) {
      toast.error("Name and group are required");
      return;
    }

    const { error } = await supabase
      .from("tags")
      .update({ name: editName.trim(), group: finalGroup })
      .eq("id", editingTag.id);

    if (error) {
      toast.error("Failed to update tag");
      return;
    }

    toast.success("Tag updated");
    handleCancelEdit();
    loadTags();
  };

  const handleCreateTag = async () => {
    const finalGroup = showNewGroupInput && newGroupName.trim() ? newGroupName.trim() : newTagGroup;
    
    if (!newTagName.trim() || !finalGroup) {
      toast.error("Name and group are required");
      return;
    }

    const { error } = await supabase
      .from("tags")
      .insert([{ name: newTagName.trim(), group: finalGroup }]);

    if (error) {
      if (error.code === "23505") {
        toast.error("A tag with this name already exists in this group");
      } else {
        toast.error("Failed to create tag");
      }
      return;
    }

    toast.success("Tag created");
    setShowCreateDialog(false);
    setNewTagName("");
    setNewTagGroup("");
    setShowNewGroupInput(false);
    setNewGroupName("");
    loadTags();
  };

  const handleStartMerge = (tag: Tag) => {
    setMergeSourceTag(tag);
    setMergeTargetId("");
    setShowMergeDialog(true);
  };

  const handleMerge = async () => {
    if (!mergeSourceTag || !mergeTargetId) {
      toast.error("Please select a target tag");
      return;
    }

    // Update all resource_tags from source to target
    const { error: updateError } = await supabase
      .from("resource_tags")
      .update({ tag_id: mergeTargetId })
      .eq("tag_id", mergeSourceTag.id);

    if (updateError) {
      toast.error("Failed to merge tags");
      return;
    }

    // Delete the source tag
    const { error: deleteError } = await supabase
      .from("tags")
      .delete()
      .eq("id", mergeSourceTag.id);

    if (deleteError) {
      toast.error("Failed to delete source tag after merge");
      return;
    }

    toast.success(`Merged "${mergeSourceTag.name}" into target tag`);
    setShowMergeDialog(false);
    setMergeSourceTag(null);
    setMergeTargetId("");
    loadTags();
  };

  const handleStartDelete = (tag: Tag) => {
    setDeleteTag(tag);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!deleteTag) return;

    // First delete all resource_tags associations
    await supabase
      .from("resource_tags")
      .delete()
      .eq("tag_id", deleteTag.id);

    // Then delete the tag
    const { error } = await supabase
      .from("tags")
      .delete()
      .eq("id", deleteTag.id);

    if (error) {
      toast.error("Failed to delete tag");
      return;
    }

    toast.success("Tag deleted");
    setShowDeleteDialog(false);
    setDeleteTag(null);
    loadTags();
  };

  const getMergeTargetOptions = () => {
    if (!mergeSourceTag) return [];
    return tags.filter(t => t.id !== mergeSourceTag.id);
  };

  if (loading) {
    return (
      <div className="p-8 space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  return (
    <div className={embedded ? "p-4 md:p-8 pt-4 space-y-6" : "p-8 space-y-6"}>
      <div className="flex items-center justify-between flex-wrap gap-4">
        {!embedded && (
          <div>
            <h1 className="text-2xl font-bold font-display">Tags Management</h1>
            <p className="text-muted-foreground">Manage tags and groups for library resources</p>
          </div>
        )}
        <div className={`flex gap-2 ${embedded ? 'w-full flex-wrap' : ''}`}>
          <Link to="/admin/library/tags/analytics">
            <Button variant="outline" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Analytics
            </Button>
          </Link>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Tag
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Tags</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{tags.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Tag Groups</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{groups.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Unused Tags</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{tags.filter(t => t.resourceCount === 0).length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tags Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Tags</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Group</TableHead>
                <TableHead className="text-center">Resources</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tags.map(tag => (
                <TableRow key={tag.id}>
                  <TableCell>
                    {editingTag?.id === tag.id ? (
                      <Input
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        className="h-8 w-40"
                      />
                    ) : (
                      <span className="font-medium">{tag.name}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingTag?.id === tag.id ? (
                      <div className="flex items-center gap-2">
                        {showNewGroupInput ? (
                          <Input
                            value={newGroupName}
                            onChange={e => setNewGroupName(e.target.value)}
                            placeholder="New group name"
                            className="h-8 w-32"
                          />
                        ) : (
                          <Select value={editGroup} onValueChange={setEditGroup}>
                            <SelectTrigger className="h-8 w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-popover border border-border z-50">
                              {groups.map(g => (
                                <SelectItem key={g} value={g}>{g}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowNewGroupInput(!showNewGroupInput)}
                          className="text-xs"
                        >
                          {showNewGroupInput ? "Existing" : "+ New"}
                        </Button>
                      </div>
                    ) : (
                      <Badge variant="outline">{tag.group}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={tag.resourceCount === 0 ? "secondary" : "default"}>
                      {tag.resourceCount}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {format(new Date(tag.created_at), "MMM d, yyyy")}
                  </TableCell>
                  <TableCell className="text-right">
                    {editingTag?.id === tag.id ? (
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" variant="ghost" onClick={handleCancelEdit}>
                          <X className="h-4 w-4" />
                        </Button>
                        <Button size="sm" onClick={handleSaveEdit}>
                          <Save className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleStartEdit(tag)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleStartMerge(tag)}>
                          <Merge className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleStartDelete(tag)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {tags.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    No tags found. Create your first tag to get started.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Tag Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Create New Tag</DialogTitle>
            <DialogDescription>Add a new tag to organize library resources.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Tag Name</label>
              <Input
                value={newTagName}
                onChange={e => setNewTagName(e.target.value)}
                placeholder="Enter tag name"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Group</label>
              {showNewGroupInput ? (
                <Input
                  value={newGroupName}
                  onChange={e => setNewGroupName(e.target.value)}
                  placeholder="Enter new group name"
                />
              ) : (
                <Select value={newTagGroup} onValueChange={setNewTagGroup}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a group" />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border border-border z-50">
                    {groups.map(g => (
                      <SelectItem key={g} value={g}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowNewGroupInput(!showNewGroupInput)}
                className="text-xs"
              >
                {showNewGroupInput ? "Use existing group" : "+ Create new group"}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTag}>Create Tag</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Dialog */}
      <Dialog open={showMergeDialog} onOpenChange={setShowMergeDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Merge Tag
            </DialogTitle>
            <DialogDescription>
              Merge "{mergeSourceTag?.name}" into another tag. All resources with this tag will be reassigned to the target tag, and the source tag will be deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Source Tag (will be deleted)</label>
              <Input value={mergeSourceTag?.name || ""} disabled />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Target Tag (will receive resources)</label>
              <Select value={mergeTargetId} onValueChange={setMergeTargetId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select target tag" />
                </SelectTrigger>
                <SelectContent className="bg-popover border border-border z-50 max-h-60">
                  {getMergeTargetOptions().map(tag => (
                    <SelectItem key={tag.id} value={tag.id}>
                      {tag.name} ({tag.group})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {mergeSourceTag && mergeSourceTag.resourceCount > 0 && (
              <p className="text-sm text-amber-500">
                ⚠️ This will move {mergeSourceTag.resourceCount} resource(s) to the target tag.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMergeDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleMerge}>Merge & Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Tag
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTag?.name}"? 
              {deleteTag && deleteTag.resourceCount > 0 && (
                <span className="block mt-2 text-destructive font-medium">
                  This tag is used by {deleteTag.resourceCount} resource(s). They will be untagged.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
